// In-app browser (embedded webview) detection + escape.
//
// Google's "use secure browsers" policy can refuse OAuth inside an embedded
// webview with `Error 403: disallowed_useragent`. When someone taps an
// innernet.live link from inside X, Instagram, LinkedIn, Facebook, the Gmail
// app, etc., the link opens in *that app's* in-app browser (a WKWebView /
// Android WebView), and sign-in can die on Google's side.
//
// ⚠️ CAN, NOT DOES. This module used to be a GATE: detect a webview and refuse
// to even attempt OAuth. That was a guess, and it was wrong — Google's block is
// not uniform across host apps, OS versions or clients (John watched a
// competitor's Google sign-in succeed inside X on iOS while ours refused to
// try). We were manufacturing a guaranteed failure to avoid a possible one, and
// reporting a 403 we had never actually seen.
//
// So detection now drives RECOVERY, never prevention. We always attempt the
// real redirect; if the user comes back having hit Google's error page, the
// breadcrumb below tells us it genuinely failed and only then do we offer the
// escape. Nothing here may ever be used to block a sign-in attempt again.

export type InAppInfo = {
  /** true when we're inside a known in-app webview */
  isInApp: boolean
  /** best-guess host app name for the message, e.g. "Instagram" */
  app: string | null
  /** 'ios' | 'android' | 'other' — drives the escape strategy */
  platform: 'ios' | 'android' | 'other'
}

// Ordered most-specific → least. `test` runs against the raw user-agent.
const SIGNATURES: Array<{ app: string; test: RegExp }> = [
  { app: 'Instagram', test: /Instagram/i },
  { app: 'Facebook', test: /FBAN|FBAV|FB_IAB|FBIOS/i },
  { app: 'Messenger', test: /Messenger/i },
  { app: 'X', test: /Twitter/i },
  { app: 'LinkedIn', test: /LinkedInApp/i },
  { app: 'Snapchat', test: /Snapchat/i },
  { app: 'TikTok', test: /BytedanceWebview|musical_ly|TikTok/i },
  { app: 'Pinterest', test: /Pinterest/i },
  { app: 'Line', test: /\bLine\//i },
  { app: 'WeChat', test: /MicroMessenger/i },
  { app: 'KakaoTalk', test: /KAKAOTALK/i },
  { app: 'WhatsApp', test: /WhatsApp/i },
  { app: 'Telegram', test: /Telegram/i },
  { app: 'Gmail', test: /GSA\//i },
]

/**
 * Inspect the current user-agent. Safe to call on the server (returns a
 * not-in-app default) — but only meaningful in the browser.
 */
export function detectInAppBrowser(ua?: string): InAppInfo {
  const agent = ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  const platform: InAppInfo['platform'] = /iPhone|iPad|iPod/i.test(agent)
    ? 'ios'
    : /Android/i.test(agent)
      ? 'android'
      : 'other'

  // 1) Named apps we recognise outright.
  for (const sig of SIGNATURES) {
    if (sig.test.test(agent)) return { isInApp: true, app: sig.app, platform }
  }

  // 2) Generic heuristic for unnamed in-app webviews.
  //    - iOS: a real Safari/Chrome UA contains "Safari"/"CriOS". A WKWebView
  //      opened by another app is missing the "Safari" token.
  //    - Android: in-app webviews carry the "; wv" token.
  const iosWebview =
    platform === 'ios' && /AppleWebKit/i.test(agent) && !/Safari/i.test(agent) && !/CriOS|FxiOS|EdgiOS/i.test(agent)
  const androidWebview = platform === 'android' && /; wv\)/i.test(agent)
  if (iosWebview || androidWebview) return { isInApp: true, app: null, platform }

  return { isInApp: false, app: null, platform }
}

/**
 * Best-effort: leave the in-app webview for the system browser.
 *
 * Android has a reliable path (an `intent://` URL that hands off to Chrome).
 * iOS has an unreliable one (`x-safari-https://`) that we now at least TRY —
 * it works on many iOS versions and silently does nothing on others, so it can
 * only ever be an attempt. Either way the caller keeps the copy-link
 * instructions on screen, because "nothing happened" is a real outcome here.
 *
 * Returns true if a hand-off was attempted — NOT that it succeeded. There is no
 * way to observe success from inside the webview; if it worked, this page is
 * already in the background.
 */
export function escapeToBrowser(url: string): boolean {
  if (typeof window === 'undefined') return false
  const info = detectInAppBrowser()
  const noScheme = url.replace(/^https?:\/\//, '')

  if (info.platform === 'android') {
    // Force Chrome, fall back to any browser.
    const intent =
      `intent://${noScheme}#Intent;scheme=https;package=com.android.chrome;` +
      `S.browser_fallback_url=${encodeURIComponent(url)};end`
    window.location.href = intent
    return true
  }

  if (info.platform === 'ios') {
    // `x-safari-https://` is undocumented and version-dependent: it hands the
    // URL to Safari where it is supported and is a no-op where it is not. A
    // no-op is harmless (WKWebView ignores an unknown scheme), so attempting it
    // strictly beats telling the user there is nothing we can do.
    window.location.href = `x-safari-https://${noScheme}`
    return true
  }

  return false
}

/* ── THE BREADCRUMB — a failure we actually saw ────────────────────────────
   We cannot catch Google's 403 in JS: it is a full-page render on Google's own
   domain, so our code is no longer running when it happens. What we CAN see is
   the user coming back. So we drop a crumb the instant before handing over to
   a provider, and read it when this page next draws. Crumb present + still in a
   webview = they left for the provider and returned without a session, which is
   the signature of the block. That is the only honest trigger for the escape
   UI, and it is worth far more than a guess made before the attempt.

   sessionStorage (not local) so the crumb dies with the tab, and it is consumed
   on read so one failure shows one notice. */

const CRUMB = 'innernet:oauth-attempt'
/** how long a crumb stays meaningful — past this it is just an old tab. */
const CRUMB_TTL_MS = 10 * 60 * 1000

/** Call immediately before redirecting to an OAuth provider. */
export function markOAuthAttempt(provider: string): void {
  try {
    sessionStorage.setItem(CRUMB, JSON.stringify({ provider, at: Date.now() }))
  } catch {
    // private mode / storage blocked — we simply lose the recovery hint
  }
}

/** Read-and-clear. Returns the provider name if a recent attempt came back. */
export function takeReturnedOAuthAttempt(): string | null {
  try {
    const raw = sessionStorage.getItem(CRUMB)
    if (!raw) return null
    sessionStorage.removeItem(CRUMB)
    const { provider, at } = JSON.parse(raw) as { provider?: string; at?: number }
    if (typeof at !== 'number' || Date.now() - at > CRUMB_TTL_MS) return null
    return provider ?? 'that provider'
  } catch {
    return null
  }
}

/** Clear the crumb — the attempt resolved, so there is nothing to recover. */
export function clearOAuthAttempt(): void {
  try {
    sessionStorage.removeItem(CRUMB)
  } catch {
    /* nothing to clear */
  }
}
